// The Okta importer stores `user/country` as the full English ISO 3166-1
// name, so official long forms and colloquial abbreviations (e.g. `UK`,
// `USA`) are included alongside the canonical name — both are what actually
// show up in hand-maintained catalog YAML. Keep this list alphabetised.
const COUNTRY_NAME_TO_CODE: Record<string, string> = {
  America: 'US',
  Belarus: 'BY',
  Belgium: 'BE',
  Canada: 'CA',
  Colombia: 'CO',
  Denmark: 'DK',
  England: 'GB',
  Estonia: 'EE',
  France: 'FR',
  Germany: 'DE',
  'Great Britain': 'GB',
  India: 'IN',
  Israel: 'IL',
  Italy: 'IT',
  Moldova: 'MD',
  Netherlands: 'NL',
  Poland: 'PL',
  Spain: 'ES',
  Sweden: 'SE',
  'U.S.': 'US',
  'U.S.A.': 'US',
  UK: 'GB',
  Ukraine: 'UA',
  'United Kingdom': 'GB',
  'United Kingdom of Great Britain and Northern Ireland': 'GB',
  'United States': 'US',
  'United States of America': 'US',
  USA: 'US',
  Vietnam: 'VN',
};

/**
 * Lowercases, collapses internal whitespace and strips `.`/`,` so that every
 * spelling of a country name funnels to a single lookup key:
 * `' U.S.A. '` -> `'usa'`, `'United  States of America'` ->
 * `'united states of america'`.
 */
function normalize(input: string): string {
  return input.trim().toLowerCase().replace(/[.,]/g, '').replace(/\s+/g, ' ');
}

/**
 * Derived from {@link COUNTRY_NAME_TO_CODE} so that table stays the single
 * source of truth and the two cannot drift apart.
 */
const NORMALIZED_LOOKUP: Record<string, string> = Object.fromEntries(
  Object.entries(COUNTRY_NAME_TO_CODE).map(([name, iso]) => [
    normalize(name),
    iso,
  ]),
);

const warnedCountries = new Set<string>();

/**
 * Surfaces a country value we could not map, once per distinct value, so a
 * residual data gap is visible in the dev console instead of silently
 * rendering a blank flag. Silent in production to avoid console noise.
 */
function warnUnresolved(raw: string, key: string): void {
  if (process.env.NODE_ENV === 'production') return;
  if (warnedCountries.has(key)) return;
  warnedCountries.add(key);
  // eslint-disable-next-line no-console
  console.warn(
    `[team-management] Unrecognised user/country value: "${raw}" — no flag rendered. Add it to COUNTRY_NAME_TO_CODE in countryFlag.ts.`,
  );
}

function isoToFlag(iso2: string): string {
  return iso2
    .toUpperCase()
    .split('')
    .map(c => String.fromCodePoint(c.charCodeAt(0) + 127397))
    .join('');
}

export function countryCodeToFlag(code: string): string {
  const trimmed = code.trim();
  if (!trimmed) return '';

  const key = normalize(trimmed);

  // Name/alias lookup runs first so `'UK'` resolves to GB rather than being
  // treated as the (unassigned) ISO 3166-1 code `UK`.
  const iso = NORMALIZED_LOOKUP[key];
  if (iso) return isoToFlag(iso);

  if (/^[a-z]{2}$/.test(key)) return isoToFlag(key);

  warnUnresolved(trimmed, key);
  return '';
}
